import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { usersApi, type UserCreateData, type UserInviteData } from '../../api/users';
import { useAuth } from '../../context/AuthContext';
import { MIN_PASSWORD_LENGTH, PASSWORD_HINT, passwordError } from '../../config/password';
import type { UserRole } from '../../types/index.js';

const ROLE_LABELS: Record<UserRole, string> = {
  system_admin: 'System Admin',
  city_admin:   'Stadt-Admin',
  member:       'Mitglied',
  read_only:    'Lesezugriff',
};

export default function UserList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: usersApi.list,
  });

  const createMutation = useMutation({
    mutationFn: (payload: UserCreateData) => usersApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowCreate(false);
    },
  });

  const inviteMutation = useMutation({
    mutationFn: (payload: UserInviteData) => usersApi.invite(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowCreate(false);
    },
  });

  if (isLoading) return <div className="p-6">Lädt…</div>;
  if (error) return <div className="p-6 text-red-600">Fehler: {(error as Error).message}</div>;

  const users = data?.items ?? [];
  const isSystemAdmin = currentUser?.role === 'system_admin';

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Nutzerverwaltung</h1>
        <div className="flex items-center gap-2">
          <Link
            to="/settings/users/invitations"
            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
          >
            Einladungen
          </Link>
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            + Neuer Nutzer
          </button>
        </div>
      </div>

      {showCreate && (
        <CreateUserForm
          isSystemAdmin={isSystemAdmin}
          onCancel={() => setShowCreate(false)}
          onCreate={(payload) => createMutation.mutate(payload)}
          onInvite={(payload) => inviteMutation.mutate(payload)}
          submitting={createMutation.isPending || inviteMutation.isPending}
          error={createMutation.error ?? inviteMutation.error}
        />
      )}

      <div className="bg-white rounded shadow border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="px-4 py-2 text-left">E-Mail</th>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Rolle</th>
              {isSystemAdmin && <th className="px-4 py-2 text-left">Stadt</th>}
              <th className="px-4 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr>
                <td colSpan={isSystemAdmin ? 5 : 4} className="px-4 py-6 text-center text-gray-500">
                  Keine Nutzer.
                </td>
              </tr>
            )}
            {users.map((u) => (
              <tr
                key={u.id}
                className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/settings/users/${u.id}`)}
              >
                <td className="px-4 py-2">{u.email}</td>
                <td className="px-4 py-2">{u.full_name}</td>
                <td className="px-4 py-2">{ROLE_LABELS[u.role]}</td>
                {isSystemAdmin && <td className="px-4 py-2 text-gray-500">{u.city_id ?? '—'}</td>}
                <td className="px-4 py-2">
                  {u.is_active ? <span className="text-green-700">aktiv</span> : <span className="text-gray-400">inaktiv</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface CreateUserFormProps {
  isSystemAdmin: boolean;
  onCancel: () => void;
  onCreate: (payload: UserCreateData) => void;
  onInvite: (payload: UserInviteData) => void;
  submitting: boolean;
  error?: unknown;
}

function CreateUserForm({ isSystemAdmin, onCancel, onCreate, onInvite, submitting, error }: CreateUserFormProps) {
  const [mode, setMode] = useState<'create' | 'invite'>('create');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<UserRole>('member');
  const [cityId, setCityId] = useState<string>('');

  const backendMessage = axios.isAxiosError(error)
    ? (error.response?.data?.detail as string | undefined)
    : undefined;
  const errorMessage =
    backendMessage ||
    (error instanceof Error ? error.message : undefined);

  const availableRoles: UserRole[] = isSystemAdmin
    ? ['system_admin', 'city_admin', 'member', 'read_only']
    : ['city_admin', 'member', 'read_only'];

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const commonPayload = {
          email,
          full_name: fullName,
          role,
          city_id: isSystemAdmin && cityId ? Number(cityId) : undefined,
        };
        if (mode === 'invite') {
          onInvite(commonPayload);
          return;
        }
        onCreate({ ...commonPayload, password });
      }}
      className="bg-white p-6 rounded shadow border border-gray-200 mb-6 space-y-3"
    >
      <h2 className="text-lg font-semibold">Neuer Nutzer</h2>
      <div className="flex gap-4 text-sm">
        <label className="inline-flex items-center gap-2">
          <input
            type="radio"
            name="create-mode"
            checked={mode === 'create'}
            onChange={() => setMode('create')}
          />
          Direkt erstellen (mit Passwort)
        </label>
        <label className="inline-flex items-center gap-2">
          <input
            type="radio"
            name="create-mode"
            checked={mode === 'invite'}
            onChange={() => setMode('invite')}
          />
          Einladung senden (Passwort durch Nutzer)
        </label>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-sm">E-Mail</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded" />
        </label>
        <label className="block">
          <span className="text-sm">Name</span>
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} required className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded" />
        </label>
        {mode === 'create' && (
          <label className="block">
            <span className="text-sm">Passwort</span>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded" />
            <span className="text-xs text-gray-500">{PASSWORD_HINT}</span>
            {passwordError(password) && (
              <span className="block text-xs text-red-600">{passwordError(password)}</span>
            )}
          </label>
        )}
        <label className="block">
          <span className="text-sm">Rolle</span>
          <select value={role} onChange={(e) => setRole(e.target.value as UserRole)} className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded">
            {availableRoles.map((r) => (
              <option key={r} value={r}>{ROLE_LABELS[r]}</option>
            ))}
          </select>
        </label>
        {isSystemAdmin && role !== 'system_admin' && (
          <label className="block col-span-2">
            <span className="text-sm">Stadt-ID (für system_admin: Pflicht außer bei Systemrolle)</span>
            <input
              type="number"
              value={cityId}
              onChange={(e) => setCityId(e.target.value)}
              required
              className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded"
            />
          </label>
        )}
      </div>

      {mode === 'invite' && (
        <p className="text-xs text-gray-500">
          Der Nutzer wird erst angelegt, nachdem er den Einladungslink geoeffnet und ein Passwort gesetzt hat.
        </p>
      )}

      {errorMessage && <div className="text-red-600 text-sm">{errorMessage}</div>}

      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 border border-gray-300 rounded">Abbrechen</button>
        <button type="submit" disabled={submitting || (mode === 'create' && password.length < MIN_PASSWORD_LENGTH)} className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
          {submitting ? 'Speichern...' : mode === 'invite' ? 'Einladung senden' : 'Erstellen'}
        </button>
      </div>
    </form>
  );
}
